<template>
  <el-table
    ref="periodicTable"
    :data="sortedData"
    :border="true"
    class="sk-table"
    style="width: 100%"
    v-loading="loading"
    @selection-change="handleSelectionChange"
  >
    <!-- Checkbox -->
    <el-table-column type="selection" width="45" align="center" />

    <!-- STT -->
    <el-table-column label="STT" type="index" width="50" align="center" />

    <!-- Tên học sinh -->
    <el-table-column label="Tên học sinh" prop="studentName" min-width="180">
      <template slot-scope="{ row }">
        <div class="pev-student-cell">
          <span class="pev-student-name" :class="{ 'pev-student-name--leave': !isStudying(row) }">
            {{ row.studentName }}
          </span>
          <el-tag
            v-if="!isStudying(row)"
            size="mini"
            type="info"
            class="pev-leave-tag"
          >
            Đã chuyển
          </el-tag>
        </div>
      </template>
    </el-table-column>

    <!-- Số lần -->
    <el-table-column label="Số lần" width="100" align="center">
      <template slot-scope="{ row }">
        <span class="pev-period-count">{{ getPeriodCount(row) }}</span>
      </template>
    </el-table-column>

    <!-- Trạng thái duyệt -->
    <el-table-column label="Trạng thái" width="130" align="center">
      <template slot-scope="{ row }">
        <template v-if="row.evaluations && row.evaluations.length > 0">
          <el-tag
            size="mini"
            :type="getOverallApprovalType(row)"
          >
            {{ getOverallApprovalLabel(row) }}
          </el-tag>
        </template>
        <span v-else class="text-slate-400">—</span>
      </template>
    </el-table-column>

    <!-- Lần gần nhất -->
    <el-table-column label="Lần gần nhất" min-width="250">
      <template slot-scope="{ row }">
        <template v-if="row.evaluations && row.evaluations.length > 0">
          <div class="pev-latest-eval">
            <el-tag size="mini" type="info">Lần {{ getLatestEvaluation(row).periodNumber }}</el-tag>
            <span class="pev-latest-content">{{ truncateContent(getLatestEvaluation(row).content) }}</span>
          </div>
        </template>
        <span v-else class="text-slate-400">Chưa có nhận xét</span>
      </template>
    </el-table-column>

    <!-- File/ảnh đính kèm của lần gần nhất -->
    <el-table-column label="File" width="80" align="center">
      <template slot-scope="{ row }">
        <template v-if="row.evaluations && row.evaluations.length > 0">
          <span v-if="getFileCount(getLatestEvaluation(row)) > 0" class="pev-file-count">
            <a href="#" @click.prevent="$emit('view-files', getEvaluationFiles(getLatestEvaluation(row)))">
              <i class="el-icon-paperclip" /> {{ getFileCount(getLatestEvaluation(row)) }}
            </a>
          </span>
          <span v-else class="text-slate-400">—</span>
        </template>
        <span v-else class="text-slate-400">—</span>
      </template>
    </el-table-column>

    <!-- Phản hồi -->
    <el-table-column label="Phản hồi" width="90" align="center">
      <template slot-scope="{ row }">
        <span class="pev-reply-badge" :class="{ 'pev-reply-badge--active': getTotalReplies(row) > 0 }">
          <i class="el-icon-chat-dot-round" /> {{ getTotalReplies(row) }}
        </span>
      </template>
    </el-table-column>

    <!-- Thao tác -->
    <el-table-column label="Thao tác" width="250" align="center" fixed="right">
      <template slot-scope="{ row }">
        <div class="flex flex-nowrap justify-center gap-1">
          <el-tooltip v-if="isStudying(row)" content="Tạo nhận xét" placement="top">
            <el-button
              type="success"
              icon="el-icon-plus"
              size="mini"
              circle
              plain
              @click="$emit('add', row)"
            />
          </el-tooltip>
          <el-tooltip content="Xem" placement="top">
            <el-button
              type="primary"
              icon="el-icon-view"
              size="mini"
              circle
              plain
              @click="$emit('view', row)"
            />
          </el-tooltip>
          <el-tooltip v-if="row.evaluations && row.evaluations.length > 0" content="Sửa" placement="top">
            <el-button
              type="primary"
              icon="el-icon-edit"
              size="mini"
              circle
              plain
              @click="$emit('edit', row)"
            />
          </el-tooltip>
          <el-tooltip
            v-if="row.evaluations && row.evaluations.length > 0 && hasUnapproved(row)"
            content="Duyệt"
            placement="top"
          >
            <el-button
              type="success"
              icon="el-icon-check"
              size="mini"
              circle
              plain
              @click="$emit('approve', row)"
            />
          </el-tooltip>
          <el-tooltip v-if="row.evaluations && row.evaluations.length > 0" content="Lịch sử" placement="top">
            <el-button
              type="info"
              icon="el-icon-time"
              size="mini"
              circle
              plain
              @click="$emit('history', row)"
            />
          </el-tooltip>
          <el-tooltip v-if="row.evaluations && row.evaluations.length > 0" content="Xoá" placement="top">
            <el-button
              type="danger"
              icon="el-icon-delete"
              size="mini"
              circle
              plain
              @click="$emit('delete', row)"
            />
          </el-tooltip>
        </div>
      </template>
    </el-table-column>
  </el-table>
</template>

<script>
export default {
  name: "PeriodicEvaluationTable",
  props: {
    data: { type: Array, default: () => [] },
    loading: { type: Boolean, default: false },
  },
  computed: {
    // Sort: STUDYING first, then LEAVE_SCHOOL
    sortedData() {
      if (!this.data || this.data.length === 0) return [];
      const studying = this.data.filter(r => this.isStudying(r));
      const others = this.data.filter(r => !this.isStudying(r));
      return [...studying, ...others];
    },
  },
  methods: {
    isStudying(row) {
      return row.kidStatus === "STUDYING";
    },

    getPeriodCount(row) {
      return (row.evaluations && row.evaluations.length) || 0;
    },

    getLatestEvaluation(row) {
      if (!row.evaluations || row.evaluations.length === 0) return null;
      // Sorted by periodNumber desc, take the highest
      return row.evaluations.reduce((latest, ev) =>
        ev.periodNumber > (latest.periodNumber || 0) ? ev : latest
      , row.evaluations[0]);
    },

    getOverallApprovalType(row) {
      if (!row.evaluations || row.evaluations.length === 0) return "info";
      const allApproved = row.evaluations.every(ev => ev.approved !== false);
      if (allApproved) return "success";
      const someApproved = row.evaluations.some(ev => ev.approved !== false);
      return someApproved ? "warning" : "info";
    },

    getOverallApprovalLabel(row) {
      if (!row.evaluations || row.evaluations.length === 0) return "";
      const allApproved = row.evaluations.every(ev => ev.approved !== false);
      if (allApproved) return "Đã duyệt";
      const someApproved = row.evaluations.some(ev => ev.approved !== false);
      return someApproved ? "Duyệt một phần" : "Chờ duyệt";
    },

    hasUnapproved(row) {
      if (!row.evaluations || row.evaluations.length === 0) return false;
      return row.evaluations.some(ev => ev.approved === false);
    },

    getTotalReplies(row) {
      if (!row.evaluations || row.evaluations.length === 0) return 0;
      return row.evaluations.reduce((sum, ev) => {
        return sum + ((ev.replies && ev.replies.length) || 0);
      }, 0);
    },

    truncateContent(content) {
      if (!content) return "";
      return content.length > 60 ? content.substring(0, 60) + "..." : content;
    },

    getFileCount(evaluation) {
      return this.getEvaluationFiles(evaluation).length || Number(evaluation && evaluation.fileCount) || 0;
    },

    getEvaluationFiles(evaluation) {
      if (Array.isArray(evaluation && evaluation.fileUrls)) return evaluation.fileUrls;
      if (Array.isArray(evaluation && evaluation.files)) return evaluation.files;
      return [];
    },

    handleSelectionChange(selection) {
      this.$emit("selection-change", selection);
    },

    clearSelection() {
      this.$refs.periodicTable?.clearSelection();
    },
  },
};
</script>

<style scoped lang="scss">
@import "@/assets/style/sk-table";

.pev-student-cell {
  display: flex;
  align-items: center;
  gap: 8px;
}

.pev-student-name {
  font-weight: 600;
  color: #1e293b;
}

.pev-student-name--leave {
  color: #94a3b8;
  font-style: italic;
}

.pev-leave-tag {
  flex-shrink: 0;
}

.pev-period-count {
  font-weight: 700;
  font-size: 14px;
  color: #2563eb;
}

.pev-latest-eval {
  display: flex;
  align-items: center;
  gap: 8px;
}

.pev-latest-content {
  font-size: 13px;
  color: #475569;
  line-height: 1.5;
}

.pev-file-count {
  color: #2563eb;
  font-weight: 600;
}

.pev-reply-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 13px;
  color: #94a3b8;
  font-weight: 500;
  i { font-size: 14px; }
}

.pev-reply-badge--active {
  color: #2563eb;
  font-weight: 600;
}

.text-slate-400 { color: #94a3b8; }
.flex { display: flex; }
.flex-wrap { flex-wrap: wrap; }
.justify-center { justify-content: center; }
.gap-1 { gap: 4px; }
</style>
